import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { loadStripe, type Stripe, type StripeElements, type StripePaymentElement } from '@stripe/stripe-js'
import { useToast } from '../../state/providers/ToastProvider'
import { useAppSettings } from '../../state/providers/AppSettingsProvider'
import styles from './PintTokensDialog.module.css'

interface PintTokensDialogProps {
  open: boolean
  onClose: () => void
}

type PintState = boolean[]

type TokenState = {
  version: 1
  pints: [PintState, PintState, PintState]
  locked: [boolean, boolean, boolean]
}

const STORAGE_KEY_BASE = 'pint_tokens_v1'
const DEVICE_ID_KEY = 'pint_tokens_device_id'
const PENDING_SPEND_KEY_BASE = 'pint_tokens_pending_spend_v1'
const SPEND_SEQ_KEY_BASE = 'pint_tokens_spend_seq_v1'
const SEGMENTS_PER_PINT = 6

const getOrCreateDeviceId = (): string => {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY)
    if (existing) {
      return existing
    }

    const next = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}_${Math.random().toString(16).slice(2)}`
    window.localStorage.setItem(DEVICE_ID_KEY, next)
    return next
  } catch {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`
  }
}

const createEmptyPint = (): PintState => [false, false, false, false, false, false]

const createPintsFromSpentCount = (spent: number): [PintState, PintState, PintState] => {
  const safe = Math.max(0, Math.min(SEGMENTS_PER_PINT * 3, Math.floor(spent)))
  const make = (offset: number): PintState => {
    return Array.from({ length: SEGMENTS_PER_PINT }, (_, i) => offset + i < safe)
  }
  return [make(0), make(SEGMENTS_PER_PINT), make(SEGMENTS_PER_PINT * 2)]
}

const createDefaultTokenState = (): TokenState => ({
  version: 1,
  pints: [createEmptyPint(), createEmptyPint(), createEmptyPint()],
  locked: [false, false, false],
})

const stripePublishableKey = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim()

const stripePromiseCache = new Map<string, ReturnType<typeof loadStripe>>()

const getStripePromise = (stripeAccountId?: string | null) => {
  if (!stripePublishableKey) {
    return null
  }

  const cacheKey = (stripeAccountId || '').trim() || '__platform__'
  const existing = stripePromiseCache.get(cacheKey)
  if (existing) {
    return existing
  }

  const promise = stripeAccountId
    ? loadStripe(stripePublishableKey, { stripeAccount: stripeAccountId })
    : loadStripe(stripePublishableKey)
  stripePromiseCache.set(cacheKey, promise)
  return promise
}

const TokenCheckoutForm = ({
  clientSecret,
  stripeAccountId,
  onClose,
  onSuccess,
}: {
  clientSecret: string
  stripeAccountId?: string | null
  onClose: () => void
  onSuccess: () => void
}): React.ReactElement => {
  const { showToast } = useToast()
  const mountRef = useRef<HTMLDivElement | null>(null)
  const stripeRef = useRef<Stripe | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const paymentElementRef = useRef<StripePaymentElement | null>(null)

  const [isReady, setIsReady] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mountError, setMountError] = useState<string>('')

  useEffect(() => {
    let isCancelled = false

    const resolveCssVar = (name: string, fallback: string): string => {
      try {
        if (typeof window === 'undefined') {
          return fallback
        }
        const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim()
        return value || fallback
      } catch {
        return fallback
      }
    }

    const mount = async () => {
      setIsReady(false)
      setMountError('')

      const stripePromise = getStripePromise(stripeAccountId)
      if (!stripePromise) {
        setMountError('Stripe is not configured')
        return
      }

      try {
        const primaryColour = resolveCssVar('--primary-colour', '#f5c400')
        const primaryTextColour = resolveCssVar('--primary-text-colour', '#111111')

        const stripe = await stripePromise
        if (!stripe || isCancelled) {
          return
        }

        stripeRef.current = stripe
        const elements = stripe.elements({
          clientSecret,
          appearance: {
            theme: 'stripe',
            variables: {
              fontFamily: 'inherit',
              fontSizeBase: '16px',
              colorPrimary: primaryColour,
              colorText: primaryTextColour,
              colorTextSecondary: 'rgba(0,0,0,0.72)',
              colorTextPlaceholder: 'rgba(0,0,0,0.55)',
              colorDanger: '#d32f2f',
              borderRadius: '12px',
              spacingUnit: '4px',
            },
            rules: {
              '.Tab': {
                color: 'rgba(0,0,0,0.85)',
              },
              '.Tab--selected': {
                color: 'rgba(0,0,0,0.92)',
              },
              '.Link': {
                color: primaryColour,
              },
              '.Link:hover': {
                color: primaryColour,
              },
              '.RedirectText': {
                color: primaryColour,
              },
              '.LinkButton': {
                color: primaryColour,
              },
              '.LinkButton:hover': {
                color: primaryColour,
              },
              '.TabIcon': {
                color: primaryColour,
              },
              '.TabLabel': {
                color: 'rgba(0,0,0,0.85)',
              },
              '.Input': {
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: 'none',
                fontSize: '16px',
              },
              '.Input:focus': {
                borderColor: primaryColour,
              },
              '.p-EmailField .Label': {
                color: 'rgba(0,0,0,0.85)',
              },
              '.p-EmailField .p-FieldLabel': {
                color: 'rgba(0,0,0,0.85)',
              },
              '.Label': {
                color: 'rgba(255,255,255,0.92)',
              },
            },
          },
        })
        elementsRef.current = elements

        const paymentElement = elements.create('payment')
        paymentElementRef.current = paymentElement

        if (mountRef.current) {
          paymentElement.mount(mountRef.current)
        }

        if (!isCancelled) {
          setIsReady(true)
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load payment form'
        if (!isCancelled) {
          setMountError(message)
          showToast(message)
        }
      }
    }

    mount()

    return () => {
      isCancelled = true
      try {
        paymentElementRef.current?.unmount()
      } catch {
        // ignore
      }
      paymentElementRef.current = null
      elementsRef.current = null
      stripeRef.current = null
    }
  }, [clientSecret, showToast, stripeAccountId])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      const stripe = stripeRef.current
      const elements = elementsRef.current
      if (!stripe || !elements) {
        return
      }

      setIsSubmitting(true)
      try {
        const result = await stripe.confirmPayment({
          elements,
          redirect: 'if_required',
        })

        if (result.error) {
          showToast(result.error.message || 'Payment failed')
          return
        }

        showToast('Payment successful')
        onSuccess()
        onClose()
      } finally {
        setIsSubmitting(false)
      }
    },
    [onClose, onSuccess, showToast],
  )

  return (
    <form onSubmit={handleSubmit}>
      {!isReady && !mountError ? (
        <Typography sx={{ fontSize: '0.95rem', opacity: 0.75, mb: 1 }}>Loading payment form…</Typography>
      ) : null}
      {mountError ? (
        <Typography sx={{ fontSize: '0.95rem', opacity: 0.75, mb: 1 }}>{mountError}</Typography>
      ) : null}
      <Box ref={mountRef} sx={{ minHeight: 170, width: '100%', overflowX: 'hidden' }} />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="text" onClick={onClose} disabled={isSubmitting} sx={{ color: 'var(--primary-colour)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={!isReady || isSubmitting}
          sx={{
            backgroundColor: 'var(--primary-colour)',
            color: 'var(--primary-text-colour)',
            '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary-colour) 88%, black)' },
          }}
        >
          Pay
        </Button>
      </Box>
    </form>
  )
}

const PintGlassToken = ({
  pintIndex,
  segments,
  onToggleSegment,
  onFillSegment,
  onSetSegment,
  fillAnimKey,
}: {
  pintIndex: number
  segments: PintState
  onToggleSegment: (segmentIndex: number) => void
  onFillSegment: (segmentIndex: number) => void
  onSetSegment: (segmentIndex: number, nextValue: boolean) => void
  fillAnimKey?: number
}): React.ReactElement => {
  const dragActiveRef = useRef(false)
  const pointerDownRef = useRef(false)
  const dragStartRef = useRef<{ x: number; y: number; segmentIndex: number } | null>(null)
  const dragModeRef = useRef<'fill' | 'empty' | null>(null)
  const lastDragSegmentRef = useRef<number | null>(null)

  const tokenFill = 'var(--primary-colour)'
  const viewBoxWidth = 95.690888
  const viewBoxHeight = 167.91893
  const clipId = useMemo(() => `pint-tokens-clip-${pintIndex}-${fillAnimKey ?? 0}`, [fillAnimKey, pintIndex])
  const beerGradId = useMemo(() => `pint-tokens-beer-grad-${pintIndex}-${fillAnimKey ?? 0}`, [fillAnimKey, pintIndex])
  const animName = useMemo(() => `pintTokensFill_${pintIndex}_${fillAnimKey ?? 0}`.replace(/[^a-zA-Z0-9_-]/g, '_'), [fillAnimKey, pintIndex])
  const waveName = useMemo(() => `pintTokensWave_${pintIndex}_${fillAnimKey ?? 0}`.replace(/[^a-zA-Z0-9_-]/g, '_'), [fillAnimKey, pintIndex])
  const bubblesName = useMemo(() => `pintTokensBubbles_${pintIndex}_${fillAnimKey ?? 0}`.replace(/[^a-zA-Z0-9_-]/g, '_'), [fillAnimKey, pintIndex])
  const foamName = useMemo(() => `pintTokensFoam_${pintIndex}_${fillAnimKey ?? 0}`.replace(/[^a-zA-Z0-9_-]/g, '_'), [fillAnimKey, pintIndex])
  const revealName = useMemo(() => `pintTokensReveal_${pintIndex}_${fillAnimKey ?? 0}`.replace(/[^a-zA-Z0-9_-]/g, '_'), [fillAnimKey, pintIndex])
  const glassInteriorPathD =
    'm 40.665906,167.30369 c -0.800365,-0.0576 -4.015052,-0.27883 -7.14375,-0.49171 -5.432185,-0.36961 -11.172063,-1.17555 -12.303125,-1.72749 -0.291041,-0.14203 -1.362604,-0.51051 -2.38125,-0.81887 -6.105646,-1.84822 -7.486932,-3.81732 -7.824165,-11.15378 -0.223892,-4.87074 -0.562715,-8.83319 -0.876478,-10.2502 -0.08056,-0.3638 -0.2796641,-2.86411 -0.4424641,-5.55625 -0.365055,-6.0367 -1.791597,-27.42774 -1.949743,-29.23646 -0.06362,-0.7276 -0.36902,-5.84729 -0.678668,-11.377076 -0.601755,-10.7463 -1.768631,-28.94366 -1.968053,-30.69167 -0.06641,-0.58208 -0.252486,-2.9038 -0.413509,-5.15937 -0.333399,-4.67019 -0.772749,-7.10476 -1.862154,-10.31875 C 0.38157986,43.320659 -0.05691214,38.227662 1.1254489,30.810606 2.0346519,25.107096 2.1485209,22.727074 1.8026879,16.655398 0.87780586,0.41754883 0.90895686,1.5302108 1.3664329,1.0726608 1.9225409,0.51646283 92.993625,0.25280483 94.024315,0.80440983 c 0.972,0.52019997 0.98614,-0.1436 -0.29828,13.99890517 -0.6399,7.045927 -0.52008,9.90139 0.63843,15.213541 0.92122,4.224121 0.80107,12.079303 -0.23674,15.478124 -0.62883,2.059402 -1.97256,6.611546 -2.65546,8.995834 -0.64984,2.26887 -0.66848,2.45998 -1.58697,16.27187 -0.43548,6.54844 -0.914,12.91828 -1.06339,14.15521 -0.14939,1.23693 -0.57326,7.24959 -0.94194,13.36146 -0.78875,13.075656 -1.26896,20.306026 -1.81878,27.384366 -0.22041,2.83766 -0.58696,7.71922 -0.81456,10.84792 -0.22759,3.1287 -0.58398,7.7126 -0.79198,10.18646 -0.208,2.47385 -0.43251,5.21229 -0.49892,6.08541 -0.61973,8.14787 -2.63101,10.05566 -13.3919,12.7028 -5.04371,1.24073 -22.85845,2.32362 -29.897919,1.81738 z'
  const glassInteriorViewBoxWidth = 95.503029
  const glassInteriorViewBoxHeight = 167.93193
  const glassInteriorScaleX = viewBoxWidth / glassInteriorViewBoxWidth
  const glassInteriorScaleY = viewBoxHeight / glassInteriorViewBoxHeight
  const segmentPaths = useMemo(
    () => [
      {
        id: 'path1',
        d: 'm 148.79906,48.118791 -45.63649,0.07855 v 47.271533 h 45.6184 l 0.18397,-0.624768 0.52555,-1.719791 0.52503,-1.719792 0.24288,-2.62723 0.2434,-2.62723 -0.2434,-3.590478 -0.24288,-3.590479 -0.28835,-1.322916 -0.28888,-1.322917 -0.36483,-1.984375 -0.36484,-1.984375 -0.008,-2.778125 -0.008,-2.778125 0.39636,-4.365625 0.39636,-4.365625 0.37259,-4.327384 0.37258,-4.327385 -0.12712,-0.331763 -0.12764,-0.331762 -0.5886,-0.315226 z',
      },
      {
        id: 'path2',
        d: 'm 100.7994,48.201473 -43.645395,0.07596 -0.42478,0.424263 -0.424264,0.42478 0.283704,4.999179 0.283187,4.999694 0.294039,5.159375 0.293522,5.159375 -0.147795,2.38125 -0.148311,2.38125 -0.674894,4.233334 -0.674895,4.233333 -5.16e-4,2.27273 -5.17e-4,2.273246 0.287838,2.22467 0.287837,2.225187 0.517281,2.116667 0.411862,1.683101 H 100.7994 Z',
      },
      {
        id: 'path3',
        d: 'm 58.076946,97.831519 0.108521,0.319877 0.762227,2.248954 0.403593,1.98438 0.403593,1.98437 0.293005,4.10105 0.292489,4.10104 0.120923,1.05833 0.120923,1.05833 0.641821,9.78959 0.642338,9.78958 0.562756,10.05417 0.556039,9.92652 H 100.7994 V 97.831519 Z',
      },
      {
        id: 'path4',
        d: 'm 103.16257,97.831519 v 56.416191 h 39.57691 l 0.50281,-8.33902 0.67076,-11.1125 0.2713,-2.24896 0.27182,-2.24896 0.79168,-11.90625 0.79169,-11.90625 0.15141,-1.32292 0.15141,-1.32291 0.49248,-1.71979 0.49247,-1.7198 0.75758,-2.568831 z',
      },
      {
        id: 'path5',
        d: 'm 63.187745,156.61088 0.03566,0.41031 0.769462,11.50937 0.768945,11.50938 0.296106,4.89479 0.29559,4.89479 0.146761,0.66146 0.146244,0.66146 0.248047,2.24896 0.248047,2.24895 0.234094,5.09065 0.234094,5.09065 0.56534,1.12706 0.565857,1.12706 1.509469,1.21853 1.509985,1.21905 0.79375,0.40514 0.79375,0.40566 1.852084,0.56069 1.852083,0.56069 0.529167,0.25787 0.529166,0.25838 3.042709,0.54105 3.042708,0.54105 5.688542,0.38706 5.688541,0.38706 1.455209,0.1049 1.454691,0.10438 3.315554,-0.0429 v -58.38351 z',
      },
      {
        id: 'path6',
        d: 'm 103.16257,156.61088 v 58.35302 l 1.33273,-0.017 7.01146,-0.0904 3.175,-0.28319 3.175,-0.28319 3.30729,-0.40824 3.30729,-0.40824 1.45521,-0.35812 1.45521,-0.35812 1.98437,-0.54002 1.98438,-0.54002 1.05833,-0.46198 1.05834,-0.46147 1.90737,-1.26866 1.9079,-1.26917 0.65267,-1.12345 0.65319,-1.12345 0.30489,-1.98437 0.30438,-1.98438 0.12092,-1.5875 0.1204,-1.5875 0.37828,-4.49791 0.37827,-4.49792 0.41393,-5.68854 0.41341,-5.68854 0.40101,-5.15938 0.40049,-5.15937 0.37414,-5.55625 0.37362,-5.55625 0.0248,-0.41031 z',
      },
    ],
    [],
  )

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{ touchAction: 'none', display: 'block' }}
      onPointerMove={(e) => {
        if (!pointerDownRef.current) {
          return
        }

        const start = dragStartRef.current
        if (!start) {
          return
        }

        if (!dragActiveRef.current) {
          const dx = e.clientX - start.x
          const dy = e.clientY - start.y
          if (dx * dx + dy * dy >= 36) {
            dragActiveRef.current = true
            const mode = dragModeRef.current
            if (mode === 'fill') {
              onSetSegment(start.segmentIndex, true)
            } else if (mode === 'empty') {
              onSetSegment(start.segmentIndex, false)
            }
          } else {
            return
          }
        }

        const el = document.elementFromPoint(e.clientX, e.clientY) as Element | null
        const segEl = (el as any)?.closest?.('[data-segment-index]') as HTMLElement | null
        const rawIndex = segEl?.dataset?.segmentIndex
        if (!rawIndex) {
          return
        }

        const idx = Number(rawIndex)
        if (!Number.isFinite(idx)) {
          return
        }

        if (lastDragSegmentRef.current === idx) {
          return
        }
        lastDragSegmentRef.current = idx

        const mode = dragModeRef.current
        if (mode === 'fill') {
          onSetSegment(idx, true)
        } else if (mode === 'empty') {
          onSetSegment(idx, false)
        }
      }}
      onPointerUp={(e) => {
        if (pointerDownRef.current) {
          const start = dragStartRef.current
          const wasDrag = dragActiveRef.current
          if (!wasDrag && start) {
            onToggleSegment(start.segmentIndex)
          }

          try {
            ;(e.currentTarget as SVGSVGElement).releasePointerCapture(e.pointerId)
          } catch {
            // ignore
          }
        }

        pointerDownRef.current = false
        dragStartRef.current = null
        dragActiveRef.current = false
        dragModeRef.current = null
        lastDragSegmentRef.current = null
      }}
      onPointerCancel={(e) => {
        try {
          ;(e.currentTarget as SVGSVGElement).releasePointerCapture(e.pointerId)
        } catch {
          // ignore
        }
        pointerDownRef.current = false
        dragStartRef.current = null
        dragActiveRef.current = false
        dragModeRef.current = null
        lastDragSegmentRef.current = null
      }}
      onLostPointerCapture={() => {
        pointerDownRef.current = false
        dragStartRef.current = null
        dragActiveRef.current = false
        dragModeRef.current = null
        lastDragSegmentRef.current = null
      }}
    >
      {fillAnimKey ? (
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path d={glassInteriorPathD} transform={`scale(${glassInteriorScaleX} ${glassInteriorScaleY})`} />
          </clipPath>
          <linearGradient id={beerGradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f4d77a" stopOpacity="0.96" />
            <stop offset="55%" stopColor="#d8a63a" stopOpacity="0.96" />
            <stop offset="100%" stopColor="#b97c1c" stopOpacity="0.98" />
          </linearGradient>
        </defs>
      ) : null}

      {fillAnimKey ? (
        <g clipPath={`url(#${clipId})`}>
          <style>
            {`
            @keyframes ${animName} {
              0% { transform: translateY(${viewBoxHeight}px); opacity: 0.0; }
              10% { opacity: 0.96; }
              72% { transform: translateY(0px); opacity: 0.96; }
              100% { transform: translateY(0px); opacity: 0; }
            }
            @keyframes ${waveName} {
              0% { transform: translateX(0px); }
              100% { transform: translateX(-26px); }
            }
            @keyframes ${bubblesName} {
              0% { transform: translateY(0px); opacity: 0; }
              15% { opacity: 0.35; }
              70% { opacity: 0.35; }
              100% { transform: translateY(-34px); opacity: 0; }
            }
            @keyframes ${foamName} {
              0% { transform: translateY(14px) scaleY(0.15); opacity: 0; }
              40% { transform: translateY(6px) scaleY(0.65); opacity: 0.6; }
              58% { transform: translateY(0px) scaleY(1); opacity: 0.95; }
              90% { transform: translateY(0px) scaleY(1); opacity: 0.95; }
              100% { transform: translateY(0px) scaleY(1); opacity: 0; }
            }
            @keyframes ${revealName} {
              0% { opacity: 0; }
              76% { opacity: 0; }
              90% { opacity: 1; }
              100% { opacity: 1; }
            }
          `}
          </style>

          <g style={{ transformOrigin: '0 0', animation: `${animName} 1400ms cubic-bezier(0.2, 0.95, 0.2, 1) forwards` }}>
            <rect x={0} y={0} width={viewBoxWidth} height={viewBoxHeight} fill={`url(#${beerGradId})`} opacity={0.96} />
            <g style={{ mixBlendMode: 'screen', opacity: 0.25 }}>
              <rect x={6} y={10} width={10} height={viewBoxHeight - 20} rx={6} fill="#ffffff" />
              <rect x={22} y={18} width={6} height={viewBoxHeight - 36} rx={6} fill="#ffffff" />
              <rect x={72} y={14} width={8} height={viewBoxHeight - 28} rx={6} fill="#ffffff" />
            </g>

            <g style={{ animation: `${bubblesName} 1200ms ease-out forwards`, opacity: 0.35 }}>
              <circle cx={26} cy={136} r={1.6} fill="#ffffff" opacity={0.55} />
              <circle cx={34} cy={124} r={1.1} fill="#ffffff" opacity={0.4} />
              <circle cx={58} cy={142} r={1.4} fill="#ffffff" opacity={0.5} />
              <circle cx={66} cy={128} r={1.0} fill="#ffffff" opacity={0.35} />
              <circle cx={46} cy={150} r={0.9} fill="#ffffff" opacity={0.3} />
            </g>

            <g style={{ animation: `${waveName} 950ms linear infinite`, opacity: 0.18 }}>
              <path
                d={`M -30 46 C -15 44, -5 48, 10 46 C 25 44, 35 48, 50 46 C 65 44, 75 48, 90 46 C 105 44, 115 48, 130 46 V 0 H -30 Z`}
                fill="#ffffff"
              />
              <path
                d={`M -30 47.5 C -15 45.5, -5 49.5, 10 47.5 C 25 45.5, 35 49.5, 50 47.5 C 65 45.5, 75 49.5, 90 47.5 C 105 45.5, 115 49.5, 130 47.5`}
                stroke="#ffffff"
                strokeWidth={1.2}
                opacity={0.28}
                fill="none"
              />
            </g>

            <g style={{ transformOrigin: '0 22px', animation: `${foamName} 1400ms ease-out forwards` }}>
              <path
                d={`M -30 22 C -8 14, 0 30, 18 22 C 34 16, 44 32, 62 22 C 78 16, 88 32, 104 22 C 122 14, 128 32, 140 22 V -6 H -30 Z`}
                fill="#f7f4ea"
                opacity={0.96}
              />
              <path
                d={`M -30 22 C -8 14, 0 30, 18 22 C 34 16, 44 32, 62 22 C 78 16, 88 32, 104 22 C 122 14, 128 32, 140 22`}
                stroke="#ffffff"
                strokeWidth={1.2}
                opacity={0.7}
                fill="none"
              />
            </g>
          </g>
        </g>
      ) : null}

      <g
        id={`pint-tokens-${pintIndex}`}
        transform="translate(-55.312478,-47.618575)"
        style={fillAnimKey ? { opacity: 0, animation: `${revealName} 1400ms ease-out forwards` } : undefined}
      >
        {segmentPaths.map((seg, segmentIndex) => (
          <path
            key={`${pintIndex}-${seg.id}`}
            d={seg.d}
            data-segment-index={segmentIndex}
            data-testid={`pint-token-segment-${pintIndex}-${segmentIndex}`}
            onPointerDown={(e) => {
              e.preventDefault()
              e.stopPropagation()

              pointerDownRef.current = true
              dragStartRef.current = { x: e.clientX, y: e.clientY, segmentIndex }
              dragActiveRef.current = false
              dragModeRef.current = segments[segmentIndex] ? 'empty' : 'fill'
              lastDragSegmentRef.current = segmentIndex
              try {
                ;(e.currentTarget.ownerSVGElement as SVGSVGElement | null)?.setPointerCapture(e.pointerId)
              } catch {
                // ignore
              }
            }}
            style={{
              fill: segments[segmentIndex] ? 'rgba(236, 240, 241, 0.45)' : tokenFill,
              stroke: 'rgba(255,255,255,0.95)',
              strokeWidth: 1.6,
              opacity: segments[segmentIndex] ? 0.42 : 1,
              cursor: 'pointer',
              transition: 'opacity 220ms ease, fill 220ms ease',
            }}
          />
        ))}
      </g>
    </svg>
  )
}

const PintTokensDialog: React.FC<PintTokensDialogProps> = ({ open, onClose }) => {
  const { showToast } = useToast()
  const { getClientId, getPintTokensPackPriceGbpPence, getPintTokensPackSize } = useAppSettings()
  const clientId = getClientId()
  const deviceId = useMemo(() => getOrCreateDeviceId(), [])
  const packPriceGbpPence = getPintTokensPackPriceGbpPence()
  const packSize = getPintTokensPackSize()
  const storageKey = useMemo(() => (clientId ? `${STORAGE_KEY_BASE}_${clientId}` : STORAGE_KEY_BASE), [clientId])
  const pendingSpendKey = useMemo(
    () => (clientId ? `${PENDING_SPEND_KEY_BASE}_${clientId}_${deviceId}` : `${PENDING_SPEND_KEY_BASE}_${deviceId}`),
    [clientId, deviceId],
  )
  const spendSeqKey = useMemo(
    () => (clientId ? `${SPEND_SEQ_KEY_BASE}_${clientId}_${deviceId}` : `${SPEND_SEQ_KEY_BASE}_${deviceId}`),
    [clientId, deviceId],
  )
  const paymentsApiBaseUrl = (process.env.REACT_APP_PAYMENTS_API_URL || '').trim()

  // Temporarily force sales open for local testing
  const isSalesOpenNow = true

  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string>('')
  const [checkoutStripeAccountId, setCheckoutStripeAccountId] = useState<string | null>(null)

  const [serverTokenBalance, setServerTokenBalance] = useState<number | null>(null)
  const [displayTokenBalance, setDisplayTokenBalance] = useState<number | null>(null)
  const [purchaseFillAnim, setPurchaseFillAnim] = useState<{ seq: number; pintIndex: number } | null>(null)
  const purchaseFillSeqRef = useRef(0)
  const [pendingSpend, setPendingSpend] = useState(0)
  const [nextSpendSeq, setNextSpendSeq] = useState(1)
  const [dragReleaseTick, setDragReleaseTick] = useState(0)
  const checkoutBaselineBalanceRef = useRef<number>(0)
  const checkoutBaselinePintIndexRef = useRef<number>(0)
  const [spendConfirmOpen, setSpendConfirmOpen] = useState(false)
  const [spendConfirmTokens, setSpendConfirmTokens] = useState(0)

  const packPriceLabel = useMemo(() => {
    const pounds = (packPriceGbpPence / 100).toFixed(2)
    return `£${pounds}`
  }, [packPriceGbpPence])

  useEffect(() => {
    if (!checkoutOpen) {
      return
    }

    const scrollY = window.scrollY
    const prevBodyOverflow = document.body.style.overflow
    const prevBodyPosition = document.body.style.position
    const prevBodyTop = document.body.style.top
    const prevBodyWidth = document.body.style.width
    const prevHtmlOverflow = document.documentElement.style.overflow

    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow
      document.body.style.overflow = prevBodyOverflow
      document.body.style.position = prevBodyPosition
      document.body.style.top = prevBodyTop
      document.body.style.width = prevBodyWidth
      window.scrollTo(0, scrollY)
    }
  }, [checkoutOpen])

  const [tokenState, setTokenState] = useState<TokenState>(() => createDefaultTokenState())
  const [activeIndex, setActiveIndex] = useState(0)
  const [draftPints, setDraftPints] = useState<[PintState, PintState, PintState]>(() => [createEmptyPint(), createEmptyPint(), createEmptyPint()])
  const draftPintsRef = useRef(draftPints)
  const prevActiveIndexRef = useRef<number>(0)
  const prevDraftSpentCountsRef = useRef<[number, number, number]>([0, 0, 0])
  const spendCommitInFlightRef = useRef(false)
  const [spendCommitInFlight, setSpendCommitInFlight] = useState(false)
  const tapDedupeRef = useRef<{ key: string; ts: number } | null>(null)
  const dragFillActiveRef = useRef(false)
  const lastDragFillKeyRef = useRef<string | null>(null)
  const prevPendingSpendRef = useRef(pendingSpend)
  const pendingSpendRef = useRef(pendingSpend)
  const nextSpendSeqRef = useRef(nextSpendSeq)

  useEffect(() => {
    draftPintsRef.current = draftPints
  }, [draftPints])

  useEffect(() => {
    const prevPending = prevPendingSpendRef.current
    prevPendingSpendRef.current = pendingSpend
    pendingSpendRef.current = pendingSpend

    void prevPending
  }, [pendingSpend])

  useEffect(() => {
    nextSpendSeqRef.current = nextSpendSeq
  }, [nextSpendSeq])

  const fetchServerBalance = useCallback(async (): Promise<number | null> => {
    if (!paymentsApiBaseUrl) {
      return null
    }

    if (!clientId) {
      return null
    }

    try {
      const url = new URL(`${paymentsApiBaseUrl.replace(/\/+$/, '')}/api/tokens/balance`)
      url.searchParams.set('deviceId', deviceId)
      url.searchParams.set('clientId', clientId)

      const res = await fetch(url.toString(), {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      })
      if (!res.ok) {
        return null
      }

      const data = await res.json().catch(() => null)
      const balance = Number(data?.balance)
      if (!Number.isFinite(balance) || balance < 0) {
        return null
      }

      return balance
    } catch {
      return null
    }
  }, [clientId, deviceId, paymentsApiBaseUrl])

  useEffect(() => {
    if (!open) {
      return
    }

    spendCommitInFlightRef.current = false
    setSpendCommitInFlight(false)

    try {
      const rawPending = window.localStorage.getItem(pendingSpendKey)
      const rawSeq = window.localStorage.getItem(spendSeqKey)
      const pending = Number(rawPending)
      const seq = Number(rawSeq)
      setPendingSpend(Number.isFinite(pending) && pending > 0 ? Math.floor(pending) : 0)
      setNextSpendSeq(Number.isFinite(seq) && seq > 0 ? Math.floor(seq) : 1)
    } catch {
      setPendingSpend(0)
      setNextSpendSeq(1)
    }
  }, [open, pendingSpendKey, spendSeqKey])

  useEffect(() => {
    if (!open) {
      return
    }

    try {
      if (pendingSpend > 0) {
        window.localStorage.setItem(pendingSpendKey, String(pendingSpend))
      } else {
        window.localStorage.removeItem(pendingSpendKey)
      }
      window.localStorage.setItem(spendSeqKey, String(nextSpendSeq))
    } catch {
      return
    }
  }, [nextSpendSeq, open, pendingSpend, pendingSpendKey, spendSeqKey])

  useEffect(() => {
    if (!open) {
      return
    }

    let cancelled = false
    ;(async () => {
      const balance = await fetchServerBalance()
      if (cancelled) {
        return
      }

      if (typeof balance === 'number') {
        setServerTokenBalance(balance)
        setDisplayTokenBalance(Math.max(0, balance - pendingSpendRef.current))
      } else {
        setServerTokenBalance((prev) => prev)
        setDisplayTokenBalance((prev) => (typeof prev === 'number' ? prev : null))
      }
    })()

    return () => {
      cancelled = true
    }
  }, [fetchServerBalance, open])

  useEffect(() => {
    if (!open) {
      return
    }

    if (typeof serverTokenBalance !== 'number') {
      return
    }

    setPendingSpend((prev) => Math.min(prev, serverTokenBalance))
  }, [open, serverTokenBalance])

  useEffect(() => {
    if (!open) {
      return
    }

    try {
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) {
        return
      }

      const parsed = JSON.parse(raw) as TokenState
      if (parsed?.version !== 1 || !Array.isArray(parsed?.pints) || parsed.pints.length !== 3) {
        return
      }

      setTokenState({
        version: 1,
        pints: parsed.pints,
        locked: Array.isArray((parsed as any).locked) && (parsed as any).locked.length === 3 ? (parsed as any).locked : [false, false, false],
      })
    } catch {
      return
    }
  }, [open, storageKey])

  useEffect(() => {
    setDraftPints(tokenState.pints.map((p) => [...p]) as [PintState, PintState, PintState])
  }, [tokenState.pints])

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(tokenState))
    } catch {
      return
    }
  }, [tokenState, storageKey])

  const countSegments = useCallback((pints: [PintState, PintState, PintState]) => {
    return pints.reduce((acc, pint) => acc + pint.filter(Boolean).length, 0)
  }, [])

  const stagedSpend = useMemo(() => {
    const committed = countSegments(tokenState.pints)
    const drafted = countSegments(draftPints)
    return Math.max(0, drafted - committed)
  }, [countSegments, draftPints, tokenState.pints])

  useEffect(() => {
    if (!open) {
      return
    }

    if (spendCommitInFlightRef.current) {
      return
    }

    if (pendingSpendRef.current > 0) {
      return
    }

    if (stagedSpend > 0) {
      return
    }

    if (typeof serverTokenBalance !== 'number') {
      return
    }

    if (!(packSize > 0)) {
      return
    }

    const safeBalance = Math.max(0, Math.min(packSize, Math.floor(serverTokenBalance)))
    const expectedSpent = Math.max(0, packSize - safeBalance)
    const expectedPints = createPintsFromSpentCount(expectedSpent)

    const currentCommitted = countSegments(tokenState.pints)
    if (currentCommitted === expectedSpent) {
      return
    }

    setTokenState((prev) => ({ ...prev, pints: expectedPints }))
    setDraftPints(expectedPints.map((p) => [...p]) as [PintState, PintState, PintState])
  }, [countSegments, open, packSize, serverTokenBalance, stagedSpend, tokenState.pints])

  const balanceKnown = typeof displayTokenBalance === 'number'
  const tokensRemaining = Math.max(0, (displayTokenBalance ?? 0) - stagedSpend)
  const canSpendTokens = balanceKnown && tokensRemaining > 0

  const availablePintIndices = [0, 1, 2] as const

  const unfilledPintIndices = useMemo(() => {
    return draftPints
      .map((pint, idx) => ({ idx, spent: pint.filter(Boolean).length }))
      .filter((item) => item.spent < 6)
      .map((item) => item.idx)
  }, [draftPints])

  const hasAnyPintsLeft = unfilledPintIndices.length > 0 && canSpendTokens
  const isFinalisingSpend = pendingSpend > 0 || spendCommitInFlight

  useEffect(() => {
    if (!open) {
      return
    }

    if (!hasAnyPintsLeft) {
      return
    }

    if (dragFillActiveRef.current) {
      return
    }

    const current = draftPintsRef.current[activeIndex]
    if (!current) {
      return
    }

    const currentSpent = current.filter(Boolean).length
    const prevSpent = prevDraftSpentCountsRef.current[activeIndex] ?? 0
    prevDraftSpentCountsRef.current = draftPintsRef.current.map((p) => p.filter(Boolean).length) as [number, number, number]

    if (currentSpent < 6) {
      return
    }

    if (prevSpent >= 6) {
      return
    }

    const ordered = [0, 1, 2]
    const start = ordered.indexOf(activeIndex)
    for (let i = 1; i <= ordered.length; i += 1) {
      const nextIdx = ordered[(start + i) % ordered.length]
      const next = draftPintsRef.current[nextIdx]
      if (next && next.filter(Boolean).length < 6) {
        setActiveIndex(nextIdx)
        return
      }
    }
  }, [activeIndex, dragReleaseTick, hasAnyPintsLeft, open])

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => {
      const ordered = [0, 1, 2]
      const currentPos = ordered.indexOf(prev)
      const safePos = currentPos >= 0 ? currentPos : 0
      const nextPos = (safePos - 1 + ordered.length) % ordered.length
      return ordered[nextPos]
    })
  }, [])

  const goNext = useCallback(() => {
    setActiveIndex((prev) => {
      const ordered = [0, 1, 2]
      const currentPos = ordered.indexOf(prev)
      const safePos = currentPos >= 0 ? currentPos : 0
      const nextPos = (safePos + 1) % ordered.length
      return ordered[nextPos]
    })
  }, [])

  // Intentionally no automatic flush on open/unmount/visibility.

  const handleClose = useCallback(() => {
    setDraftPints(tokenState.pints.map((p) => [...p]) as [PintState, PintState, PintState])
    onClose()
  }, [onClose, tokenState.pints])

  const handleBackdropPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.target !== e.currentTarget) {
        return
      }
      handleClose()
    },
    [handleClose],
  )

  useEffect(() => {
    if (!open) {
      return
    }

    const onUp = () => {
      dragFillActiveRef.current = false
      lastDragFillKeyRef.current = null
      setDragReleaseTick((n) => n + 1)
    }

    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [open])

  const commitSpend = useCallback(async (tokensToSpend: number) => {
    if (!paymentsApiBaseUrl || !clientId) {
      showToast('Payments API not configured')
      return
    }

    if (!balanceKnown) {
      return
    }

    if (spendCommitInFlightRef.current) {
      return
    }

    if (tokensToSpend <= 0) {
      return
    }

    const seqToCommit = nextSpendSeqRef.current
    pendingSpendRef.current = tokensToSpend
    setPendingSpend(tokensToSpend)
    setDisplayTokenBalance((prev) => (typeof prev === 'number' ? Math.max(0, prev - tokensToSpend) : prev))

    spendCommitInFlightRef.current = true
    setSpendCommitInFlight(true)
    try {
      const deviceId = getOrCreateDeviceId()
      const res = await fetch(`${paymentsApiBaseUrl.replace(/\/+$/, '')}/api/tokens/spend-commit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deviceId, clientId, seq: seqToCommit, tokens: tokensToSpend }),
      })

      const data = await res.json().catch(() => null)
      if (!res.ok) {
        const message = data?.error || `Token spend failed (${res.status})`
        if (!message.includes('')) {
          // no-op
        }
        setDisplayTokenBalance((prev) => (typeof prev === 'number' ? prev + tokensToSpend : prev))
        showToast(message)
        return
      }

      const nextBalance = Number(data?.balance)
      if (Number.isFinite(nextBalance) && nextBalance >= 0) {
        setServerTokenBalance(nextBalance)
        setDisplayTokenBalance(nextBalance)
      }

      setTokenState((prevState) => ({
        ...prevState,
        pints: draftPintsRef.current.map((p) => [...p]) as [PintState, PintState, PintState],
      }))
      setNextSpendSeq((prev) => (prev === seqToCommit ? prev + 1 : prev))
    } catch {
      setDisplayTokenBalance((prev) => (typeof prev === 'number' ? prev + tokensToSpend : prev))
      showToast('Token spend request failed')
    } finally {
      pendingSpendRef.current = 0
      setPendingSpend(0)
      spendCommitInFlightRef.current = false
      setSpendCommitInFlight(false)
    }
  }, [balanceKnown, clientId, paymentsApiBaseUrl, showToast])

  const handleSubmitSpend = useCallback(() => {
    if (!balanceKnown) {
      return
    }

    if (spendCommitInFlightRef.current) {
      return
    }

    if (stagedSpend <= 0) {
      return
    }

    setSpendConfirmTokens(stagedSpend)
    setSpendConfirmOpen(true)
  }, [balanceKnown, stagedSpend])

  useEffect(() => {
    prevActiveIndexRef.current = activeIndex
  }, [activeIndex])

  const toggleSegment = useCallback(
    (segmentIndex: number) => {
      if (!balanceKnown) {
        return
      }

      if (spendCommitInFlightRef.current) {
        return
      }

      if (pendingSpendRef.current > 0) {
        return
      }

      const committed = tokenState.pints[activeIndex]
      if (committed?.[segmentIndex]) {
        return
      }

      const currentDraft = draftPintsRef.current
      const current = currentDraft[activeIndex]
      const nextPint = [...current]
      const nextValue = !nextPint[segmentIndex]

      if (nextValue && tokensRemaining <= 0) {
        return
      }

      const now = Date.now()
      const tapKey = `${activeIndex}:${segmentIndex}`
      const last = tapDedupeRef.current
      if (last && last.key === tapKey && now - last.ts < 420) {
        return
      }
      tapDedupeRef.current = { key: tapKey, ts: now }

      nextPint[segmentIndex] = nextValue
      const nextDraft = currentDraft.map((p, idx) => (idx === activeIndex ? nextPint : p)) as [PintState, PintState, PintState]

      setDraftPints(nextDraft)
    },
    [activeIndex, balanceKnown, tokenState.pints, tokensRemaining],
  )

  const setSegment = useCallback(
    (segmentIndex: number, nextValue: boolean) => {
      if (!balanceKnown) {
        return
      }

      if (spendCommitInFlightRef.current) {
        return
      }

      if (pendingSpendRef.current > 0) {
        return
      }

      const committed = tokenState.pints[activeIndex]
      if (committed?.[segmentIndex]) {
        return
      }

      if (nextValue && tokensRemaining <= 0) {
        return
      }

      const currentDraft = draftPintsRef.current
      const current = currentDraft[activeIndex]
      if (!current) {
        return
      }

      if (current[segmentIndex] === nextValue) {
        return
      }

      const nextPint = [...current]
      nextPint[segmentIndex] = nextValue
      const nextDraft = currentDraft.map((p, idx) => (idx === activeIndex ? nextPint : p)) as [PintState, PintState, PintState]
      setDraftPints(nextDraft)
    },
    [activeIndex, balanceKnown, tokenState.pints, tokensRemaining],
  )

  const fillSegment = useCallback(
    (segmentIndex: number) => {
      if (!balanceKnown) {
        return
      }

      if (tokensRemaining <= 0) {
        return
      }

      if (spendCommitInFlightRef.current) {
        return
      }

      if (pendingSpendRef.current > 0) {
        return
      }

      const committed = tokenState.pints[activeIndex]
      if (committed?.[segmentIndex]) {
        return
      }

      const currentDraft = draftPintsRef.current
      const current = currentDraft[activeIndex]
      if (current?.[segmentIndex]) {
        return
      }

      const dragKey = `${activeIndex}:${segmentIndex}`
      if (dragFillActiveRef.current && lastDragFillKeyRef.current === dragKey) {
        return
      }

      dragFillActiveRef.current = true
      lastDragFillKeyRef.current = dragKey

      const nextPint = [...current]
      nextPint[segmentIndex] = true
      const nextDraft = currentDraft.map((p, idx) => (idx === activeIndex ? nextPint : p)) as [PintState, PintState, PintState]
      setDraftPints(nextDraft)
    },
    [activeIndex, balanceKnown, tokenState.pints, tokensRemaining],
  )
  const pintsRemaining = useMemo(() => {
    return Math.floor(tokensRemaining / SEGMENTS_PER_PINT)
  }, [tokensRemaining])

  const pintsLeftFromTokens = useMemo(() => tokensRemaining / SEGMENTS_PER_PINT, [tokensRemaining])
  const pintsLeftLabel = useMemo(() => {
    if (Number.isInteger(pintsLeftFromTokens)) {
      return `${pintsLeftFromTokens}`
    }

    if (Math.abs(pintsLeftFromTokens * 2 - Math.round(pintsLeftFromTokens * 2)) < 0.0001) {
      return pintsLeftFromTokens.toFixed(1)
    }

    return pintsLeftFromTokens.toFixed(2)
  }, [pintsLeftFromTokens])

  const handleBuyMoreTokens = useCallback(async () => {
    if (!isSalesOpenNow) {
      showToast('Token sales are currently closed')
      return
    }

    if (!paymentsApiBaseUrl) {
      showToast('Payments API not configured')
      return
    }

    if (!stripePublishableKey) {
      showToast('Stripe publishable key not configured')
      return
    }

    if (!clientId) {
      showToast('Client not configured')
      return
    }

    checkoutBaselineBalanceRef.current = typeof serverTokenBalance === 'number' ? serverTokenBalance : typeof displayTokenBalance === 'number' ? displayTokenBalance : 0
    checkoutBaselinePintIndexRef.current = activeIndex

    const deviceId = getOrCreateDeviceId()

    try {
      const res = await fetch(`${paymentsApiBaseUrl.replace(/\/+$/, '')}/api/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deviceId, clientId }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        const message = data?.error || `Payment error (${res.status})`
        showToast(message)
        return
      }

      const data = await res.json().catch(() => null)
      const secret = data?.clientSecret
      const stripeAccountId = typeof data?.stripeAccountId === 'string' ? data.stripeAccountId : null
      if (!secret || typeof secret !== 'string') {
        showToast('Payment initialisation failed')
        return
      }

      setCheckoutClientSecret(secret)
      setCheckoutStripeAccountId(stripeAccountId)
      setCheckoutOpen(true)
    } catch {
      showToast('Payment API request failed')
    }
  }, [activeIndex, clientId, displayTokenBalance, isSalesOpenNow, paymentsApiBaseUrl, serverTokenBalance, showToast])

  const handleCheckoutSuccess = useCallback(async () => {
    const baseline = checkoutBaselineBalanceRef.current
    setCheckoutOpen(false)
    setCheckoutStripeAccountId(null)

    await new Promise((resolve) => {
      window.setTimeout(resolve, 500)
    })

    let lastSeenBalance: number | null = null

    for (let attempt = 0; attempt < 20; attempt += 1) {
      const balance = await fetchServerBalance()
      if (typeof balance === 'number') {
        setServerTokenBalance(balance)
        setDisplayTokenBalance(Math.max(0, balance - pendingSpendRef.current))
        lastSeenBalance = balance

        if (balance > baseline) {
          pendingSpendRef.current = 0
          setPendingSpend(0)
          setTokenState(createDefaultTokenState())
          setDisplayTokenBalance(balance)
          purchaseFillSeqRef.current += 1
          setPurchaseFillAnim({ seq: purchaseFillSeqRef.current, pintIndex: checkoutBaselinePintIndexRef.current })
          return
        }
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, 1000)
      })
    }

    if (typeof lastSeenBalance === 'number') {
      const seenBalance = lastSeenBalance
      setServerTokenBalance(seenBalance)
      setDisplayTokenBalance(Math.max(0, seenBalance - pendingSpendRef.current))
    }
    showToast('Payment received but token balance has not updated yet')
  }, [fetchServerBalance, showToast])

  useEffect(() => {
    if (!purchaseFillAnim) {
      return
    }

    const t = window.setTimeout(() => {
      setPurchaseFillAnim((prev) => (prev && prev.seq === purchaseFillAnim.seq ? null : prev))
    }, 1550)

    return () => {
      window.clearTimeout(t)
    }
  }, [purchaseFillAnim])

  if (!open) {
    return null
  }

  return (
    <Box className={styles.container} onPointerDown={handleBackdropPointerDown}>
      <Box
        className={styles.modalContent}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Box className={styles.hero}>
          <IconButton onClick={handleClose} className={styles.closeButton} aria-label="Close">
            <CloseIcon sx={{ fontSize: '1.9rem' }} />
          </IconButton>
          <Box className={styles.heroContent}>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--primary-colour)' }}>Tokens</Typography>
          </Box>
        </Box>

        <Box className={styles.content}>
          <Box
            sx={{
              width: '100%',
              maxWidth: 520,
              mx: 'auto',
              mb: 1,
              px: 1.5,
              py: 1,
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 94%, white)',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--primary-colour)', lineHeight: 1.25 }}>
              {tokensRemaining} tokens left ({pintsLeftLabel} pints)
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', opacity: 0.9, mt: 0.25 }}>
              {pintsRemaining} full pints remaining to spend
            </Typography>
          </Box>

          {balanceKnown ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', pb: 1 }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 360,
                    height: 'min(46vh, 380px)',
                    maxHeight: 'min(46vh, 380px)',
                    aspectRatio: '5 / 7',
                    margin: '0 auto',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <PintGlassToken
                    pintIndex={activeIndex}
                    segments={draftPints[activeIndex]}
                    onToggleSegment={toggleSegment}
                    onFillSegment={fillSegment}
                    onSetSegment={setSegment}
                    fillAnimKey={purchaseFillAnim && purchaseFillAnim.pintIndex === activeIndex ? purchaseFillAnim.seq : undefined}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  position: 'relative',
                  zIndex: 2,
                }}
              >
                <Tooltip title="Previous pint" arrow>
                  <span>
                    <IconButton
                      aria-label="Previous pint"
                      onClick={goPrev}
                      disabled={availablePintIndices.length <= 1}
                      sx={{
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        borderRadius: '12px',
                        color: 'var(--primary-colour)',
                        backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 86%, black)',
                        '&:hover': { backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 78%, black)' },
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, minWidth: 64 }}>
                  {availablePintIndices.map((idx) => (
                    <Box
                      key={idx}
                      onClick={() => setActiveIndex(idx)}
                      sx={{
                        width: idx === activeIndex ? 18 : 8,
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: idx === activeIndex ? 'var(--primary-colour)' : 'rgba(255,255,255,0.35)',
                        transition: 'all 160ms ease',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>

                <Tooltip title="Next pint" arrow>
                  <span>
                    <IconButton
                      aria-label="Next pint"
                      onClick={goNext}
                      disabled={availablePintIndices.length <= 1}
                      sx={{
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        borderRadius: '12px',
                        color: 'var(--primary-colour)',
                        backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 86%, black)',
                        '&:hover': { backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 78%, black)' },
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {tokensRemaining <= 0 && stagedSpend <= 0 ? (
                  <Button
                    variant="contained"
                    onClick={handleBuyMoreTokens}
                    disabled={!isSalesOpenNow}
                    sx={{
                      minWidth: 180,
                      backgroundColor: 'var(--primary-colour)',
                      color: 'var(--secondary-colour)',
                      '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary-colour) 88%, black)' },
                    }}
                  >
                    Buy more tokens
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSubmitSpend}
                    disabled={stagedSpend <= 0 || spendCommitInFlight}
                    sx={{
                      minWidth: 140,
                      backgroundColor: 'var(--primary-colour)',
                      color: 'var(--secondary-colour)',
                      '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary-colour) 88%, black)' },
                    }}
                  >
                    Submit
                  </Button>
                )}
              </Box>
            </Box>
          ) : isFinalisingSpend ? (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', opacity: 0.9 }}>
                Finishing up…
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', opacity: 0.9 }}>
                You're out of tokens.
              </Typography>
              <Button
                variant="contained"
                onClick={handleBuyMoreTokens}
                disabled={!isSalesOpenNow}
                sx={{
                  backgroundColor: 'var(--primary-colour)',
                  color: 'var(--primary-text-colour)',
                  '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary-colour) 88%, black)' },
                }}
              >
                Buy more tokens
              </Button>
            </Box>
          )}
        </Box>

        {stripePublishableKey && checkoutOpen && checkoutClientSecret ? (
          <Box
            onClick={() => setCheckoutOpen(false)}
            sx={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              zIndex: 20000,
              display: 'grid',
              placeItems: 'center',
              p: 2,
              height: '100dvh',
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: 'min(520px, 100%)',
                maxHeight: 'min(90vh, 720px)',
                borderRadius: '14px',
                backgroundColor: 'var(--secondary-colour)',
                p: 2,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 18px 48px rgba(0,0,0,0.5)',
              }}
            >
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, mb: 0.25, color: 'var(--primary-colour)' }}>Buy tokens</Typography>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1, color: 'rgba(255,255,255,0.9)', opacity: 0.9 }}>
                {packSize} tokens — {packPriceLabel}
              </Typography>
              <TokenCheckoutForm
                clientSecret={checkoutClientSecret}
                stripeAccountId={checkoutStripeAccountId}
                onClose={() => setCheckoutOpen(false)}
                onSuccess={handleCheckoutSuccess}
              />
            </Box>
          </Box>
        ) : null}

        <Dialog
          open={spendConfirmOpen}
          onClose={() => setSpendConfirmOpen(false)}
          disablePortal
          hideBackdrop
          sx={{ zIndex: 3000 }}
          PaperProps={{
            sx: {
              width: 'min(520px, 92vw)',
              borderRadius: '14px',
              backgroundColor: 'var(--secondary-colour)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              color: 'rgba(255,255,255,0.92)',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: 'var(--primary-colour)' }}>Confirm spend</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'rgba(255,255,255,0.92)' }}>
              Spend {spendConfirmTokens} tokens?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1.25 }}>
            <Button
              variant="text"
              onClick={() => setSpendConfirmOpen(false)}
              disabled={spendCommitInFlight}
              sx={{ color: 'var(--primary-colour)' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                const tokensToSpend = spendConfirmTokens
                setSpendConfirmOpen(false)
                await commitSpend(tokensToSpend)
              }}
              disabled={spendCommitInFlight || spendConfirmTokens <= 0}
              sx={{
                backgroundColor: 'var(--primary-colour)',
                color: 'var(--secondary-colour)',
                '&:hover': { backgroundColor: 'color-mix(in srgb, var(--primary-colour) 88%, black)' },
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  )
}

export default PintTokensDialog
